import React, { useEffect, useState } from "react";
import Topbar from "../../components/Topbar/Topbar";
import { useLocation } from "react-router-dom";
import { get } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";

const emptyState = {
  userId: "",
  userName: "",
  requestId: "",
  requestDate: "",
  requestTime: "",
  state: "",
  city: "",
  area: "",
  roomRequiredDate: "",
  bookingStatus: "",
  bookingId: "",
  bookedDate: "",
  propertyId: "",
  propertyName: "",
  notificationSent: "",
  whatsappSent: "",
  message: "",
};

// Read-only, null-safe field
function Field({ id, label, value, icon }) {
  const safeValue = value ?? "";
  return (
    <div style={styles.field}>
      <label htmlFor={id} style={styles.label}>
        {label}
      </label>
      <div style={styles.inputWrap}>
        <input
          id={id}
          type="text"
          value={safeValue}
          readOnly
          style={{
            ...styles.input,
            color: safeValue ? "#374151" : "#9CA3AF",
          }}
        />
        {icon && <span style={styles.icon}>{icon}</span>}
      </div>
    </div>
  );
}

// Replace null/undefined with "" so inputs never receive null
const clean = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v ?? ""]));

export default function BookingRequestForm() {
  const [form, setForm] = useState(emptyState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const location = useLocation();
  const bookingData = location?.state?.bookingData;
  const recordId = bookingData?.id;

  useEffect(() => {
    if (!recordId) return;

    const fetchingData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await get(
          `${URLS.AI_BOOKING_HISTORY_DETAIL}${recordId}`,
        );
        // if (!response.ok) {
        //   throw new Error(`HTTP error! status: ${response.status}`);
        // }
        const d = response?.data;
        console.log("Fetched booking data:", response);
        // Nested sections first, flat keys as fallback
        setForm({
          ...emptyState,
          ...clean(d.userDetails),
          ...clean(d.requirementDetails),
          ...clean(d.bookingStatusDetail),
          bookingStatus:
            d.bookingStatus ?? d.bookingStatusDetail?.bookingStatus ?? "",
          message: d.bookingStatusDetail?.message ?? "",
          notificationSent:
            d.reminderDetails?.notificationSent ?? d.notificationSent ?? "",
          whatsappSent: d.reminderDetails?.whatsappSent ?? d.whatsappSent ?? "",
        });
      } catch (err) {
        console.error("Error fetching booking data:", err);
        setError("Could not load booking details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchingData();
  }, [recordId]);

  const isBooked =
    (form.bookingStatus || bookingData?.bookingStatus) !== "not_booked";
  const onCancel = () => {
    window.history.back();
  };
  return (
    <>
      <Topbar header="AI-Booked History" />

      <div style={styles.page}>
        <div style={styles.card}>
          {loading && <p style={styles.statusText}>Loading...</p>}
          {error && <p style={styles.reminderText}>{error}</p>}

          <section>
            <h2 style={styles.sectionTitle}>User Details</h2>
            <div style={styles.grid2}>
              <Field id="userId" label="User ID" value={form.userId} />
              <Field id="userName" label="User Name" value={form.userName} />
            </div>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Requirement Details</h2>
            <div style={styles.grid3}>
              <Field id="requestId" label="Request ID" value={form.requestId} />
              <Field
                id="requestDate"
                label="Request Date"
                value={form.requestDate}
                icon="📅"
              />
              <Field
                id="requestTime"
                label="Request Time"
                value={form.requestTime}
                icon="🕒"
              />
            </div>
            <div style={styles.grid3}>
              <Field id="state" label="State" value={form.state} />
              <Field id="city" label="City" value={form.city} />
              <Field id="area" label="Area" value={form.area} />
            </div>
            <div style={styles.grid3}>
              <Field
                id="roomRequiredDate"
                label="Room Required Date"
                value={form.roomRequiredDate}
                icon="📅"
              />
            </div>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={{ color: "#EF4444", marginRight: 6 }}>♥</span>
              Booking Status
            </h2>

            {!isBooked && (
              <p style={styles.reminderText}>
                *
                {form.message ||
                  "User has not made a booking. Reminder process is active"}
              </p>
            )}

            {isBooked && (
              <>
                <div style={styles.grid2}>
                  <Field
                    id="bookingId"
                    label="Booking ID"
                    value={form.bookingId}
                  />
                  <Field
                    id="bookedDate"
                    label="Booked Date"
                    value={form.bookedDate}
                    icon="📅"
                  />
                </div>
                <div style={styles.grid2}>
                  <Field
                    id="propertyId"
                    label="Property ID"
                    value={form.propertyId}
                    icon="🔗"
                  />
                  <Field
                    id="propertyName"
                    label="Property Name"
                    value={form.propertyName}
                    icon="🔗"
                  />
                </div>
              </>
            )}

            <div style={styles.grid2}>
              <Field
                id="notificationSent"
                label="Notification sent"
                value={form.notificationSent}
              />
              <Field
                id="whatsappSent"
                label="WhatsApp sent"
                value={form.whatsappSent}
              />
            </div>
          </section>

          <div style={styles.footer}>
            <button style={styles.cancelBtn} onClick={onCancel}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  page: {},
  card: {
    maxWidth: 1220,
  },
  section: {
    marginTop: 28,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 16,
  },
  statusText: {
    color: "#6B7280",
    fontSize: 13,
    marginBottom: 16,
  },
  reminderText: {
    color: "#EF4444",
    fontSize: 13,
    fontWeight: 500,
    marginTop: -4,
    marginBottom: 16,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    marginBottom: 20,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 20,
    marginBottom: 20,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 13,
    color: "#6B7280",
    fontWeight: 500,
  },
  inputWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 36px 10px 12px",
    background: "#F3F4F6",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    outline: "none",
  },
  icon: {
    position: "absolute",
    right: 10,
    fontSize: 14,
    color: "#9CA3AF",
    pointerEvents: "none",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 32,
  },
  cancelBtn: {
    background: "#D8B4FE",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "10px 28px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};
